import { NavLink, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Users, ShieldCheck, LayoutDashboard, ArrowLeft, FileUp, Server, Building2, Heart, Settings, ClipboardList } from "lucide-react";
import AdminAdmins from "./AdminAdmins";
import AdminUsers from "./AdminUsers";
import AdminClients from "./AdminClients";
import AdminImport from "./AdminImport";
import AdminBackend from "./AdminBackend";
import AdminClinicalSettings from "./AdminClinicalSettings";
import AdminGeneral from "./AdminGeneral";

const navItems = [
  { to: "/admin/general",   label: "General",   icon: Settings },
  { to: "/admin/admins",    label: "Admins",    icon: ShieldCheck },
  { to: "/admin/usuarios",  label: "Usuarios",  icon: Users },
  { to: "/admin/clientes",  label: "Clientes",  icon: Building2 },
  { to: "/admin/importar",  label: "Importados", icon: FileUp },
  { to: "/admin/backend",   label: "Backend",   icon: Server },
  { to: "/admin/clinica",   label: "Suite Clínica", icon: Heart },
];

const headerMap = {
  "/admin/general":  { title: "Configuración General", subtitle: "Ajustes globales del sistema e integraciones" },
  "/admin/admins":   { title: "Administradores",   subtitle: "Gestiona los roles de administrador" },
  "/admin/usuarios": { title: "Usuarios",           subtitle: "Listado completo y gestión de accesos" },
  "/admin/clientes": { title: "Clientes",           subtitle: "Empresas B2B y B2G — los usuarios individuales son B2C por defecto" },
  "/admin/importar": { title: "Datos importados",   subtitle: "Registros pre-existentes de hojas de cálculo" },
  "/admin/backend":  { title: "Backend GCP",        subtitle: "Monitor de sincronización y consulta de datos" },
  "/admin/clinica":  { title: "Suite Clínica",     subtitle: "Configuración unificada de umbrales, guías de la OMS y programas clínicos metabólicos" },
};

export default function AdminPanel() {
  const { user } = useAuth();
  const location = useLocation();

  const currentPath = Object.keys(headerMap).find((p) => location.pathname.startsWith(p)) || "/admin/admins";
  const header = headerMap[currentPath] || headerMap["/admin/admins"];

  return (
    <div className="min-h-screen flex" style={{ background: "#F1F5F9" }}>
      {/* Sidebar */}
      <aside
        className="w-56 flex flex-col shrink-0"
        style={{ background: "#1E293B" }}
        data-testid="admin-sidebar"
      >
        <div className="px-4 py-5 border-b" style={{ borderColor: "#334155" }}>
          <h1 className="text-base font-bold text-white tracking-tight">WE360</h1>
          <p className="text-[11px] mt-0.5" style={{ color: "#94A3B8" }}>Panel de administraci&oacute;n</p>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "text-white" : "hover:bg-white/5"
                }`
              }
              style={({ isActive }) =>
                isActive ? { background: "#334155", color: "white" } : { color: "#94A3B8" }
              }
              data-testid={`admin-nav-${item.to.split("/").pop()}`}
            >
              <item.icon size={16} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="px-3 py-4 border-t space-y-2" style={{ borderColor: "#334155" }}>
          <NavLink
            to="/dashboard"
            className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-white/5"
            style={{ color: "#94A3B8" }}
            data-testid="admin-back-to-app"
          >
            <ArrowLeft size={16} />
            Volver a la app
          </NavLink>
          <div className="px-3 py-2">
            <p className="text-xs truncate" style={{ color: "#64748B" }}>
              {user?.email}
            </p>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <header
          className="px-8 py-5 border-b"
          style={{ background: "white", borderColor: "#E2E8F0" }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ background: "#E0F2FE" }}
            >
              <LayoutDashboard size={16} style={{ color: "#0284C7" }} />
            </div>
            <div>
              <h2 className="text-lg font-bold" style={{ color: "#1E293B" }}>
                {header.title}
              </h2>
              <p className="text-xs" style={{ color: "#64748B" }}>
                {header.subtitle}
              </p>
            </div>
          </div>
        </header>

        <div className="p-8">
          <Routes>
            <Route path="general" element={<AdminGeneral />} />
            <Route path="admins" element={<AdminAdmins />} />
            <Route path="usuarios" element={<AdminUsers />} />
            <Route path="clientes" element={<AdminClients />} />
            <Route path="importar" element={<AdminImport />} />
            <Route path="backend" element={<AdminBackend />} />
            <Route path="clinica" element={<AdminClinicalSettings />} />
            <Route path="umbrales" element={<Navigate to="../clinica" replace />} />
            <Route path="oms" element={<Navigate to="../clinica" replace />} />
            <Route path="*" element={<Navigate to="/admin/general" replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
